/**
 * The chat surface itself — opening greeting + suggestion chips, streamed
 * messages, and the input row. Shares `layoutId` with MahiOrb/MahiResultsChip
 * so mounting this (and unmounting whichever of those was showing) is what
 * produces the orb→panel morph; this component owns none of that state, only
 * the panel's own concerns (focus trap, scroll-to-bottom, the input, the
 * per-turn "still thinking" label).
 *
 * `text` in each message is the fully-accumulated string (MahiChatPanel just
 * renders it), not a delta — accumulation happens one level up in
 * useMahiConversation so message history survives this component unmounting
 * when the panel closes.
 */
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, Loader2, Mic, X } from 'lucide-react';
import toast from 'react-hot-toast';
import MahiRobot from '../MahiRobot';
import MahiLogo from '../ui/MahiLogo';
import MahiThinkingIndicator from './MahiThinkingIndicator';
import useReducedMotion from '../../hooks/useReducedMotion';
import useFocusTrap from '../../hooks/useFocusTrap';
import useRotatingHint from '../../hooks/useRotatingHint';
import useVisualViewportInset from '../../hooks/useVisualViewportInset';
import { browseCategoriesFor } from './browseCategories';

// Shown one at a time in the input, not as a row of chips. Phrased as things
// someone would actually type, since that is now literally what they are —
// the field they appear in is the field you answer them in.
const DEFAULT_SUGGESTIONS = [
  'Something for a sangeet…',
  'Under ₹5,000…',
  'More like what I saved yesterday…',
  'Surprise me…',
];

function MahiStatusLabel() {
  // Purely a client-side timing heuristic — the SSE stream has no explicit
  // "tool call in progress" event, so this upgrades the copy once nothing
  // has arrived after a beat, and once more if it's still quiet later,
  // satisfying "can update once, never dead air" without any backend change.
  const [label, setLabel] = useState(null);
  useEffect(() => {
    // No specific count baked in on purpose — the catalog keeps growing, and
    // a number here would need updating by hand every time it does.
    const t1 = setTimeout(() => setLabel('Looking through our brands for you…'), 550);
    const t2 = setTimeout(() => setLabel('Still checking a few more - hang tight…'), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return <MahiThinkingIndicator label={label} />;
}

function MahiMessageBubble({ message }) {
  const { role, text, streaming, isIntro } = message;

  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-slate-900 text-white px-3.5 py-2 text-sm font-mahi-sans leading-relaxed">
          {text}
        </div>
      </div>
    );
  }

  if (streaming && !text) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-mahiteal-50/70 px-3.5 py-2.5">
          <MahiStatusLabel />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-start">
      <div
        className={`max-w-[85%] rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm leading-relaxed font-mahi-sans ${
          isIntro ? 'bg-mahiteal-50 text-mahiteal-800 ring-1 ring-mahiteal-100' : 'bg-slate-50 text-slate-800'
        }`}
      >
        {text}
        {streaming && (
          <span className="inline-block w-1.5 h-3.5 bg-mahiteal-400 ml-0.5 align-middle animate-pulse" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

export default function MahiChatPanel({
  layoutId, messages, onSend, onClose, onBrowseCategory, browsing, gender = '',
  inputHints = DEFAULT_SUGGESTIONS,
}) {
  // Which chips to lead with — the same resolved gender the feed itself
  // filters on (see prefetch.js's feedGenderFor), not re-derived here.
  const browseCategories = browseCategoriesFor(gender);

  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const reduced = useReducedMotion();
  const keyboardInset = useVisualViewportInset();
  const [draft, setDraft] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  // Deliberately no autofocus. Focusing the input on mount summoned the
  // keyboard before the user had decided to type, which on a phone meant the
  // panel opened with half of itself already covered — and the first thing
  // most people want here is a category, not a sentence. The keyboard now
  // appears when someone taps the input, which is when they want it.

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Nothing has been asked yet: the categories are the fastest route to
  // products and get the room. They go away the moment a real conversation
  // starts, because by then the answer is in the thread, not in a grid.
  const atRest = messages.length <= 1;

  // Rotates only on the opening screen, where the examples are teaching what
  // this box is for. Once there is a conversation to read, a caption cycling
  // in the corner is motion competing with her reply for no benefit — the
  // field falls back to naming itself and stays still. Frozen on focus too:
  // text moving under a cursor someone just placed reads as a glitch.
  const hint = useRotatingHint(atRest ? inputHints : ['Ask MAHI…'],
    { paused: inputFocused });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    onSend(draft);
    setDraft('');
  };

  return (
    <>
      <motion.div
        key="mahi-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduced ? 0.15 : 0.28 }}
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/55 backdrop-blur-md z-40"
        aria-hidden="true"
      />
      {/* Bottom sheet on a phone, centred card from `sm` up.
          It was a centred card everywhere, with the keyboard inset applied as
          bottom padding — so opening the keyboard squeezed the card from both
          ends and left a strip. Anchored to the bottom edge, the same inset
          simply lifts the sheet clear of the keyboard and the content above
          scrolls, which is how every native sheet behaves. */}
      <div
        className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:px-4 pointer-events-none"
        style={{ paddingBottom: keyboardInset > 0 ? keyboardInset : 0 }}
      >
        <motion.div
          ref={panelRef}
          layoutId={reduced ? undefined : layoutId}
          initial={reduced ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={reduced
            ? { duration: 0.2, ease: 'easeInOut' }
            // Slightly slower than the (implicit, symmetric) collapse
            // transition on MahiResultsChip's closing spring — "expand
            // feels weighted, slightly slower opening than closing".
            : { type: 'spring', stiffness: 200, damping: 26, mass: 1 }}
          role="dialog"
          aria-modal="true"
          aria-label="Ask MAHI"
          data-testid="mahi-chat-panel"
          className="pointer-events-auto w-full max-h-[86vh] rounded-t-[28px]
                     sm:w-[min(94vw,26rem)] sm:h-[min(76vh,42rem)] sm:max-h-none sm:rounded-[28px]
                     bg-white shadow-[0_40px_90px_-20px_rgba(15,23,42,0.45)] ring-1 ring-mahiteal-100/70 flex flex-col overflow-hidden"
        >
          {/* Grab handle — says "this is a sheet, it goes away downwards"
              before anyone has to find the close button. Phone only; a
              centred dialog has no down to go. */}
          <div className="shrink-0 pt-2.5 pb-1 flex justify-center sm:hidden">
            <div className="w-9 h-1 rounded-full bg-slate-200" />
          </div>

          <div className="shrink-0 px-4 pt-2 pb-3 sm:pt-4 flex items-center gap-2.5 border-b border-slate-100">
            <div className="w-8 h-8 rounded-full bg-mahiteal-50 ring-2 ring-mahiteal-100 flex items-center justify-center shrink-0">
              <MahiRobot size={20} />
            </div>
            <div className="flex-1 min-w-0 text-slate-900 leading-none">
              <MahiLogo className="h-3 w-auto" />
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="mahi-panel-close"
              aria-label="Close"
              className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 active:scale-90 transition-transform"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3 min-h-0">
            {messages.map((m) => <MahiMessageBubble key={m.id} message={m} />)}

            {/* Straight to products, no conversation. A tap here does not go
                through MAHI at all — see useMahiConversation.browseCategory —
                because her first turn cannot search by design, so sending
                "Sarees" as a message would come back "what's the occasion?".
                Placed above the prompts because browsing is what most people
                came to do; describing something is the richer path, not the
                common one. */}
            {atRest && (
              <div className="pt-1">
                <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-semibold font-mahi-sans mb-2">
                  Browse
                </p>
                {/* Wrapping pills, not a two-column grid. A grid gives "Tops"
                    exactly as much room as "Kurta sets", so half the width on
                    a phone went to padding around short words — twelve
                    categories took six 44px rows. Sized to the label they
                    hold, the same twelve fit in about four short ones, which
                    on a 640px screen is the difference between the grid
                    filling the sheet and leaving room for the conversation
                    underneath it. */}
                <div className="flex flex-wrap gap-1.5">
                  {browseCategories.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => onBrowseCategory?.(c)}
                      disabled={Boolean(browsing)}
                      data-testid="mahi-browse-category"
                      data-category={c.key}
                      className="h-8 pl-3 pr-3 rounded-full bg-white ring-1 ring-slate-200 text-slate-800 text-[13px] font-medium font-mahi-sans inline-flex items-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50"
                    >
                      {c.label}
                      {browsing === c.key && (
                        <Loader2 className="w-3 h-3 shrink-0 animate-spin text-mahiteal-500" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Signposts the input rather than heading a list of its own. The
              prompts that used to sit under an "Or ask" heading are now the
              input's own rotating placeholder — the same suggestions, in the
              one place where acting on them means typing anyway, and costing
              no vertical space at all. */}
          {atRest && (
            <div className="shrink-0 px-4 pb-1 flex items-center gap-2" aria-hidden="true">
              <span className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-semibold font-mahi-sans">
                Or ask MAHI
              </span>
              <span className="flex-1 h-px bg-slate-100" />
            </div>
          )}

          <form onSubmit={handleSubmit} className="shrink-0 px-3 pb-3 pt-2 border-t border-slate-100 flex items-center gap-2">
            <button
              type="button"
              data-testid="mahi-voice-input"
              aria-label="Voice input"
              onClick={() => toast("Voice input isn't wired up yet", { icon: '🎙️' })}
              className="w-10 h-10 shrink-0 rounded-full bg-slate-50 ring-1 ring-slate-200 flex items-center justify-center text-slate-500 active:scale-95 transition-transform"
            >
              <Mic className="w-[18px] h-[18px]" />
            </button>
            {/* The example prompts live here now. The real `placeholder` is
                blank and the visible one is an overlay, because a placeholder
                attribute cannot be crossfaded — swapping it outright makes the
                text jump, which on a control someone is deciding whether to
                tap reads as a rendering fault rather than a suggestion. */}
            <div className="relative flex-1 min-w-0">
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                placeholder=""
                aria-label="Ask MAHI"
                data-testid="mahi-chat-input"
                // A chat box, so: no autocorrect nagging on fashion vocabulary,
                // sentence case rather than none, and a Send key instead of the
                // generic return — the form's onSubmit already handles Enter.
                enterKeyHint="send"
                autoComplete="off"
                autoCapitalize="sentences"
                autoCorrect="off"
                className="w-full h-10 px-4 rounded-full bg-slate-50 ring-1 ring-slate-200 text-sm text-slate-900 font-mahi-sans focus:outline-none focus:ring-2 focus:ring-mahiteal-300"
              />
              {!draft && (
                <div
                  className="absolute inset-y-0 left-4 right-4 flex items-center overflow-hidden pointer-events-none"
                  aria-hidden="true"
                >
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={hint}
                      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 6 }}
                      animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
                      exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                      transition={{ duration: reduced ? 0.12 : 0.28, ease: [0.22, 1, 0.36, 1] }}
                      data-testid="mahi-input-hint"
                      className="block truncate text-sm text-slate-400 font-mahi-sans"
                    >
                      {hint}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={!draft.trim()}
              data-testid="mahi-send-button"
              aria-label="Send"
              className="w-10 h-10 shrink-0 rounded-full bg-mahiteal-500 disabled:bg-slate-200 flex items-center justify-center text-white disabled:text-slate-400 active:scale-95 transition-transform"
            >
              <ArrowUp className="w-[18px] h-[18px]" />
            </button>
          </form>
        </motion.div>
      </div>
    </>
  );
}
